/**
 * quoteBuilderHelpers.js — Iter 140.3
 * ------------------------------------
 * Pure helpers extracted from QuoteBuilder.jsx so the regex-driven
 * description sync, the reorder logic, and the auto-clearinghouse
 * trigger set can be unit-tested in isolation without mounting the
 * full component (which depends on axios + router).
 *
 * KEEP the implementations in QuoteBuilder.jsx and these helpers in
 * sync — the test suite asserts the *spec*, the component re-uses
 * these helpers directly.
 */

// Product names that, when added to a quote, should auto-add a
// "Clearinghouse Fees" line item (qty 1, rep adjusts).
export const CLEARINGHOUSE_TRIGGER_NAMES = new Set([
  "Essentials Bundle (EHR + PMS)",
  "Pro Bundle (EHR + RCM)",
  "RevQ (AI Billing)",
  "RevQ Billing App",
  "Revenue Cycle Service",
]);

/**
 * Replace the percentage literal in a product description so it stays
 * in sync with the live unit_price.
 *
 * Two formats are supported, in this priority order:
 *
 *   1) "@ 6% monthly collections"          → "@ 4% monthly collections"
 *      (Pro Bundle / RCM / RevQ style — the `@` token unambiguously
 *      tags the rate.)
 *
 *   2) Bare "<originalPrice>%" appearing anywhere in the text. Used by
 *      RPM-percentage products where the description reads
 *      "50% of reimbursement + $100 upfront per device" — when the rep
 *      bumps the price to 40 we rewrite that exact "50%" → "40%". We
 *      only touch the literal that matches the ORIGINAL catalog price so
 *      adjacent percentages with different meanings (e.g. "100% of
 *      reimbursement months 1-2") are NEVER overwritten. Iter 177
 *      (Iratzi #196720ce Feb 2026).
 */
export function syncPercentageDescription(originalDescription, livePrice, originalPrice) {
  if (!originalDescription) return originalDescription;
  // Format 1 — `@ N%` pattern (highest signal).
  const atForm = /@\s*\d+(?:\.\d+)?\s*%/;
  if (atForm.test(originalDescription)) {
    return originalDescription.replace(/@\s*\d+(?:\.\d+)?\s*%/g, `@ ${livePrice}%`);
  }
  // Format 2 — bare `<originalPrice>%` token. Requires the caller to
  // pass the catalog/original price so we know which literal to swap.
  if (originalPrice == null) return originalDescription;
  const origNum = Number(originalPrice);
  if (!Number.isFinite(origNum)) return originalDescription;
  // Word-boundary on the left so "100%" doesn't get matched when the
  // original price is "0"; trailing `%` provides the right boundary.
  const re = new RegExp(`(^|[^\\d.])${origNum}%`, "g");
  return originalDescription.replace(re, (_m, pre) => `${pre}${livePrice}%`);
}

/**
 * Iter 183 (Feb 2026 — Iratzi #7e4168c8): same idea for DOLLAR-priced
 * tokens. Several catalog entries hard-code a per-unit dollar value in
 * their description (e.g. Clearinghouse Fees: "@ $0.15 each"). When
 * the rep overrides the unit_price the description was left stale —
 * "transaction fee shows .10 in the price column but description
 * still says .15."
 *
 * Strategy: replace any `@ $N.NN` token with the live price (formatted
 * with the same precision the rep typed: 2 decimals when fractional,
 * else integer). Conservative: only `@ $...` patterns are swapped to
 * avoid clobbering unrelated dollar amounts the rep may have included.
 */
export function syncDollarDescription(originalDescription, livePrice) {
  if (!originalDescription) return originalDescription;
  const liveNum = Number(livePrice);
  if (!Number.isFinite(liveNum)) return originalDescription;
  const liveStr = liveNum % 1 === 0 ? String(liveNum) : liveNum.toFixed(2);
  return originalDescription.replace(/@\s*\$\s*\d+(?:\.\d+)?/g, `@ $${liveStr}`);
}

/**
 * Reorder a single line item from `from` to `to` index. Returns a NEW
 * array (immutable). No-ops when from === to or either is null.
 */
export function reorderLineItem(items, from, to) {
  if (from == null || to == null || from === to) return items;
  const next = [...items];
  const [moved] = next.splice(from, 1);
  next.splice(to > from ? to - 1 : to, 0, moved);
  return next;
}

/**
 * Compute the flat monthly recurring total. Excludes percentage-model
 * items (because they scale with collections, not a fixed amount) and
 * one-time fees (e.g. Implementation / Setup Fee). Also excludes
 * discount lines — they're applied as negative adjustments in
 * `applyDiscountsToSubtotal()`, not as positive recurring revenue.
 */
export function flatMonthlyRecurringTotal(lineItems) {
  return (lineItems || []).reduce((sum, item) => {
    if (item.pricing_model === "percentage") return sum;
    if (item.pricing_model === "setup_discount" || item.pricing_model === "product_discount") return sum;
    const label = String(item.unit_label || "").toLowerCase();
    if (label.includes("one-time")) return sum;
    return sum + (parseFloat(item.total) || 0);
  }, 0);
}

/**
 * Iter 140.6 — discount lifecycle.
 *
 * `getDiscountStatus(item, now)` returns one of:
 *   - "inactive"  — line is not a discount type
 *   - "active"    — discount applies in full
 *   - "warning"   — discount still active but expires within 7 days
 *   - "expired"   — past expires_at; discount auto-deactivated, total → 0
 */
const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;
export function getDiscountStatus(item, now = Date.now()) {
  if (!item) return "inactive";
  if (item.pricing_model !== "setup_discount" && item.pricing_model !== "product_discount") {
    return "inactive";
  }
  // Setup discounts don't expire. Product discounts may.
  if (item.pricing_model === "setup_discount") return "active";
  if (!item.expires_at) return "active";
  const exp = Date.parse(item.expires_at);
  if (!Number.isFinite(exp)) return "active";
  if (now >= exp) return "expired";
  if (exp - now <= SEVEN_DAYS_MS) return "warning";
  return "active";
}

/**
 * Compute the dollar amount a discount line takes off the quote.
 * Returns a POSITIVE number (the amount of reduction). Expired
 * discounts contribute 0.
 *
 * - setup_discount: applies to the Implementation / Setup Fee line
 * - product_discount: applies to the line with item.target_product
 */
export function computeDiscountReduction(item, allLineItems, now = Date.now()) {
  if (getDiscountStatus(item, now) === "expired") return 0;
  const amount = parseFloat(item.discount_amount) || 0;
  if (amount <= 0) return 0;
  // Find the target line
  let target;
  if (item.pricing_model === "setup_discount") {
    target = (allLineItems || []).find(l => l.product === "Implementation / Setup Fee");
  } else if (item.pricing_model === "product_discount") {
    target = (allLineItems || []).find(l => l.product === item.target_product);
  }
  if (!target) return 0;
  const targetTotal = parseFloat(target.total) || 0;
  if (item.discount_type === "fixed") return Math.min(amount, targetTotal);
  // percentage
  return targetTotal * (amount / 100);
}

/**
 * Sum all discount reductions across the line items.
 */
export function totalDiscountReduction(lineItems, now = Date.now()) {
  return (lineItems || []).reduce(
    (sum, item) => sum + computeDiscountReduction(item, lineItems, now),
    0,
  );
}

/**
 * Returns true when the post-save UI should LOCK the send buttons
 * because the quote is awaiting approval.
 */
export function isQuoteSendDisabled(status) {
  const s = (status || "draft").toLowerCase();
  return s === "pending_approval" || s === "pending";
}
